import { Component, OnInit, Inject, ViewChild, signal, computed } from '@angular/core';
import { FormBuilder, UntypedFormControl } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { L10N_LOCALE, L10nLocale } from 'angular-l10n';
import { AuthService } from 'src/app/shared/authentication/auth.service';
import {
  ContractOverage,
  CreateContractCommand,
  CreateContractLimitCommand,
  GlobalTenantContractsApi,
  LimitUsage,
  TenantContractLimitModel,
  TenantContractModel,
  TenantContractsApi,
  UpdateContractCommand,
  UpdateContractLimitCommand,
} from 'src/app/usage-api';
import { ContractLimitAddDialogComponent } from '../contract-limit-add-dialog/contract-limit-add-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmComponent } from 'src/app/shared/modals/confirm/confirm.component';
import { Tenant } from 'src/app/hickory-api';
import { SB_PANEL_MESSAGE, SnackbarService } from 'src/app/shared/snackbar.service';
import { MatSort } from '@angular/material/sort';
import { LayoutService } from 'src/app/shared/layout/layout.service';
import { ApiError } from 'src/app/shared/model/utility';
import { catchError, delay, map, of } from 'rxjs';
import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'app-contract-view',
  templateUrl: './contract-view.component.html',
  styleUrls: ['./contract-view.component.scss'],
  standalone: false,
})
export class ContractViewComponent implements OnInit {
  viewLimitsInTable: boolean = false;
  limitDisplayColumns = ['category', 'code', 'limitation', 'priority'];

  @ViewChild(MatSort) sort?: MatSort;
  isReadOnly: boolean = true;
  isLoading: boolean = false;
  errorFindingContract: boolean = false;
  actionName?: string;

  dataSource = new MatTableDataSource<TenantContractLimitModel>();
  limits = signal<TenantContractLimitModel[]>([]);
  hasLimits = computed(() => this.limits()?.length);
  overages = signal<TenantContractLimitModel[]>([]);
  currentOverages = computed(() => this.overages());

  create: boolean = false;
  contractId?: string | null;
  selectedTenant?: Tenant;
  tenantId?: number;

  selectedEndDate?: Date;
  selectedStartDate?: Date;
  startFormControl: UntypedFormControl = new UntypedFormControl();
  endFormControl: UntypedFormControl = new UntypedFormControl();

  apiWaitMs: number = 1000;

  contractForm = this.fb.group({
    brand: [{ value: null as string | null, disabled: this.isReadOnly }],
    tenantName: [{ value: null as string | null, disabled: this.isReadOnly }],
    serviceFee: [{ value: null as number | null, disabled: this.isReadOnly }],
    clientContact: [{ value: null as string | null, disabled: this.isReadOnly }],
    accountManager: [{ value: null as string | null, disabled: this.isReadOnly }],
    accountManagerEmail: [{ value: null as string | null, disabled: this.isReadOnly }],
    invoiceFrequency: [{ value: null as string | null, disabled: this.isReadOnly }],
    planType: [{ value: null as string | null, disabled: this.isReadOnly }],
    backOfficeNotes: [{ value: null as string | null, disabled: this.isReadOnly }],
    tenantNotes: [{ value: null as string | null, disabled: this.isReadOnly }],
    voiceOverageFee: [{ value: null as number | null, disabled: this.isReadOnly }],
    smsOverageFee: [{ value: null as number | null, disabled: this.isReadOnly }],
    isActive: [{ value: null as boolean | null, disabled: this.isReadOnly }],
  });

  selectedTabIndex: number = 0;

  tabMap: { [key: string]: number } = {
    'usage-overview': 0,
    'usage-overages': 1,
    limits: 2,
  };

  get cancelLink(): string[] {
    return ['/back-office/contracts'];
  }
  get topOffset(): string {
    return this.layoutService.layoutHeightOffsetTop + 'px';
  }
  get isSuperAdmin(): boolean {
    return this.authService.isSuperAdmin();
  }
  get isCreate(): boolean {
    return this.create;
  }
  get displayAccountManagerData(): boolean {
    const model = this.contractForm.getRawValue();
    return (
      !this.isReadOnly ||
      (this.isReadOnly && (model.accountManager?.length !== 0 || model.accountManagerEmail?.length !== 0))
    );
  }

  constructor(
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private readonly layoutService: LayoutService,
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly dialog: MatDialog,
    private readonly route: ActivatedRoute,
    private readonly tenantContractsApi: TenantContractsApi,
    private readonly globalTenantContractsApi: GlobalTenantContractsApi,
    private readonly sb: SnackbarService,
    private readonly destroyRef: DestroyRef,
  ) {}

  ngOnInit() {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const tabName: string = params['tab'];
      if (tabName && this.tabMap[tabName] !== undefined) {
        this.selectedTabIndex = this.tabMap[tabName];
      }
    });

    if (!this.isSuperAdmin && this.route.snapshot.data.action !== 'view') {
      // redirect to dashboard if not a superadmin and not in view mode
      void this.router.navigate(['dashboard']);
    }

    if (this.route.snapshot.data.action === 'view') {
      this.isReadOnly = true;
    } else {
      this.isReadOnly = false;
      this.limitDisplayColumns.push('edit');
      this.limitDisplayColumns.push('delete');
    }

    this.createFormGroup();

    if (this.route.snapshot.data.action === 'view') {
      this.actionName = 'BackOffice.ManageContracts.Contract.ViewContractButton';
      this.create = false;
      this.tenantId = this.authService.user?.snapshot?.tenantId;
      if (this.tenantId !== undefined) {
        this.getActiveContract(this.tenantId);
      }
    }

    if (this.route.snapshot.data.action === 'create') {
      this.actionName = 'BackOffice.ManageContracts.Contract.CreateButton';
      this.create = true;

      this.contractForm.patchValue({ isActive: true});

      // Initialize start date to first day of the year
      this.selectedStartDate = new Date();
      this.selectedStartDate.setMonth(0);
      this.selectedStartDate.setDate(1);
      this.startFormControl = new UntypedFormControl(this.selectedStartDate);

      // Initialize end date to last day of the year
      this.selectedEndDate = new Date();
      this.selectedEndDate.setMonth(11);
      this.selectedEndDate.setDate(31);
      this.endFormControl = new UntypedFormControl(this.selectedEndDate);
    }

    if (this.route.snapshot.data.action === 'edit') {
      this.actionName = 'BackOffice.ManageContracts.Contract.EditButton';
      const tenantId = this.route.snapshot.params['tenantId'];
      if (tenantId !== undefined) {
        this.tenantId = tenantId;
      }

      const id = this.route.snapshot.params['id'];
      if (id !== undefined) {
        this.contractId = id;
      }
    }

    if (this.contractId && this.tenantId != undefined) {
      this.isLoading = true;
      this.tenantContractsApi
        .tenantContractsFindContractById(this.tenantId.toString(), this.contractId)
        .pipe(
          map((contractResponse) => {
            if (contractResponse) {
              this.processContractResponse(contractResponse);
              this.isLoading = false;
            } else {
              this.errorFindingContract = true;
            }
          }),
          catchError((_: ApiError) => {
            this.displayErrorMessage('Error finding contract');
            this.isLoading = false;
            this.errorFindingContract = true;
            return of(null);
          }),
        )
        .subscribe();
    }
  }

  splitCode(code?: string) {
    if (!code) {
      return [];
    }

    return code
      .trim()
      .split(',')
      .map((c) => c.trim());
  }

  createFormGroup() {
    this.contractForm = this.fb.nonNullable.group({
      brand: [{ value: null as string | null, disabled: this.isReadOnly }],
      tenantName: [{ value: null as string | null, disabled: this.isReadOnly }],
      serviceFee: [{ value: null as number | null, disabled: this.isReadOnly }],
      clientContact: [{ value: null as string | null, disabled: this.isReadOnly }],
      accountManager: [{ value: null as string | null, disabled: this.isReadOnly }],
      accountManagerEmail: [{ value: null as string | null, disabled: this.isReadOnly }],
      invoiceFrequency: [{ value: null as string | null, disabled: this.isReadOnly }],
      planType: [{ value: null as string | null, disabled: this.isReadOnly }],
      backOfficeNotes: [{ value: null as string | null, disabled: this.isReadOnly }],
      tenantNotes: [{ value: null as string | null, disabled: this.isReadOnly }],
      voiceOverageFee: [{ value: null as number | null, disabled: this.isReadOnly }],
      smsOverageFee: [{ value: null as number | null, disabled: this.isReadOnly }],
      isActive: [{ value: null as boolean | null, disabled: this.isReadOnly }],
    });
  }

  getActiveContract(tenantId: number) {
    this.isLoading = true;
    this.tenantContractsApi
      .tenantContractsFindContracts(tenantId.toString(), true)
      .pipe(
        // Service returns active contracts ordered by createdDate
        map((contractsResponse) => {
          if (contractsResponse.items?.length) {
            this.processContractResponse(contractsResponse.items[0]!);
          } else {
            this.errorFindingContract = true;
          }
          this.isLoading = false;
        }),
        catchError((_: ApiError) => {
          this.displayErrorMessage('Error finding contract');
          this.isLoading = false;
          this.errorFindingContract = true;
          return of(null);
        }),
      )
      .subscribe();
  }

  processContractResponse(contract: TenantContractModel) {
    if (!contract) {
      return;
    }

    this.contractId = contract.id;
    this.contractForm.patchValue(contract);
    if (contract.contractStartDate) {
      this.selectedStartDate = new Date(contract.contractStartDate);
    }
    if (contract.servicePeriodEndDate) {
      this.selectedEndDate = new Date(contract.servicePeriodEndDate);
    }
    this.startFormControl = new UntypedFormControl(this.selectedStartDate);
    this.endFormControl = new UntypedFormControl(this.selectedEndDate);

    if (this.isReadOnly) {
      this.startFormControl.disable();
      this.endFormControl.disable();
    }

    this.getContractLimits(contract.tenantId!.toString(), contract.id!);
    this.getContractOverages(contract.tenantId!.toString(), contract.id!);
  }

  getContractLimits(tenantId: string, contractId: string) {
    this.tenantContractsApi
      .tenantContractsFindContractLimits(tenantId, contractId)
      .pipe(
        map((limitResponse) => {
          if (limitResponse?.items?.length && limitResponse.items.length > 0) {
            this.limits.set(limitResponse.items!);
            this.dataSource.data = this.limits();
          }
        }),
        catchError((_: ApiError) => {
          this.displayErrorMessage('Error getting contract limits');
          return of(null);
        }),
      )
      .subscribe();
  }

  getContractOverages(tenantId: string, contractId: string) {
    this.globalTenantContractsApi
      .globalTenantContractsFindTenantContractOverages(tenantId, contractId)
      .pipe(
        map((overageResponse) => {
          if (overageResponse.items?.length && overageResponse.items.length > 0) {
            this.overages.set(
              overageResponse.items.flatMap((item: ContractOverage) =>
                (item.overages || []).map((limitUsage: LimitUsage) => ({
                  id: limitUsage.limitId,
                  tenantId: item.tenantId,
                  contractId: item.contractId,
                  category: limitUsage.category,
                  code: limitUsage.code,
                  limitation: limitUsage.limitation,
                  priority: limitUsage.priority,
                  createdDate: null,
                  updatedDate: null,
                })),
              ) || [],
            );
          } else {
            this.overages.set([]);
          }
          return this.overages();
        }),
        catchError((_: ApiError) => {
          this.displayErrorMessage('Error getting contract overages');
          return of(null);
        }),
      )
      .subscribe();
  }

  onTenantSelected(tenant: Tenant): void {
    this.tenantId = tenant?.id ?? -1;
    this.selectedTenant = tenant;
  }

  // Common contract command building logic
  private buildBaseContractCommand(
    contractModel: TenantContractModel,
  ): Omit<CreateContractCommand | UpdateContractCommand, 'tenantName' | 'brand'> {
    return {
      isActive: contractModel.isActive ?? false,
      serviceFee: contractModel.serviceFee ?? 0.0,
      clientContact: contractModel.clientContact ?? '',
      accountManager: contractModel.accountManager ?? '',
      accountManagerEmail: contractModel.accountManagerEmail ?? '',
      contractStartDate: this.selectedStartDate?.toISOString(),
      servicePeriodEndDate: this.selectedEndDate?.toISOString(),
      invoiceFrequency: contractModel.invoiceFrequency ?? '',
      planType: contractModel.planType ?? '',
      backOfficeNotes: contractModel.backOfficeNotes ?? '',
      tenantNotes: contractModel.tenantNotes ?? '',
      voiceOverageFee: contractModel.voiceOverageFee ?? 0.0,
      smsOverageFee: contractModel.smsOverageFee ?? 0.0,
    };
  }

  buildCreateContractCommand(contractModel: TenantContractModel): CreateContractCommand {
    return {
      ...this.buildBaseContractCommand(contractModel),
      brand: this.selectedTenant?.brandName ?? '',
      tenantName: this.selectedTenant?.name ?? '',
    };
  }

  buildUpdateContractCommand(contractModel: TenantContractModel): UpdateContractCommand {
    return {
      ...this.buildBaseContractCommand(contractModel),
      brand: contractModel.brand ?? '',
      tenantName: contractModel.tenantName,
    };
  }

  // Common contract limit command building logic
  private buildBaseContractLimitCommand(limitModel: TenantContractLimitModel) {
    return {
      category: limitModel.category,
      code: limitModel.code,
      limitation: limitModel.limitation,
      priority: limitModel.priority,
    };
  }

  buildUpdateContractLimitCommand(limitModel: TenantContractLimitModel): UpdateContractLimitCommand {
    return this.buildBaseContractLimitCommand(limitModel);
  }

  buildCreateContractLimitCommand(limitModel: TenantContractLimitModel): CreateContractLimitCommand {
    return this.buildBaseContractLimitCommand(limitModel);
  }

  startDateFilter = (d: Date): boolean => {
    const now = new Date();
    return d <= now;
  };

  endDateFilter = (d: Date): boolean => {
    if (!this.selectedStartDate) {
      return false;
    }

    return d >= this.selectedStartDate;
  };

  endDateChange(event: { value: Date }) {
    if (event.value != null) {
      this.selectedEndDate = new Date(event.value);
    } else {
      this.selectedEndDate = undefined;
    }
  }

  startDateChange(event: { value: Date }) {
    if (event.value != null) {
      this.selectedStartDate = new Date(event.value);
    } else {
      this.selectedStartDate = undefined;
    }
  }

  formValid(): boolean {
    return !!this.selectedStartDate && !!this.selectedEndDate && !!this.tenantId;
  }

  onCreate(): void {
    if (this.contractForm.invalid) {
      return;
    }

    this.isLoading = true;
    const model = this.contractForm.getRawValue();
    const command = this.buildCreateContractCommand(<TenantContractModel>{
      ...model,
      isActive: model.isActive ?? false,
    });

    this.tenantContractsApi
      .tenantContractsCreateContract(this.tenantId!.toString(), command)
      .pipe(
        delay(this.apiWaitMs),
        map((_) => {
          void this.router.navigate(['back-office', 'contracts']);
          this.sb.open('Contract Created Successfully');
          this.isLoading = false;
        }),
        catchError((_: ApiError) => {
          this.displayErrorMessage('Error creating contract.');
          this.isLoading = false;
          return of(null);
        }),
      )
      .subscribe();
  }

  onUpdate(): void {
    if (this.contractForm.invalid) {
      return;
    }

    this.isLoading = true;
    const model = this.contractForm.getRawValue();
    const command = this.buildUpdateContractCommand(<TenantContractModel>{
      ...model,
      isActive: model.isActive ?? false,
    });

    this.tenantContractsApi
      .tenantContractsUpdateContract(this.tenantId!.toString(), this.contractId!.toString(), command)
      .pipe(
        delay(this.apiWaitMs),
        map((_) => {
          void this.router.navigate(['back-office', 'contracts']);
          this.sb.open('Contract Updated Successfully');
          this.isLoading = false;
        }),
        catchError((_: ApiError) => {
          this.displayErrorMessage('Error updating contract');
          this.isLoading = false;
          return of(null);
        }),
      )
      .subscribe();
  }

  onCancel(): void {
    void this.router.navigate(this.cancelLink);
  }

  confirmDeleteContractLimit(limit: TenantContractLimitModel) {
    const dialogRef = this.dialog.open(ConfirmComponent, {
      width: '30vw',
      data: { title: 'Delete', message: 'ConfirmDeleteQuestion' },
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.deleteContractLimit(limit);
      }
    });
  }

  deleteContractLimit(limit: TenantContractLimitModel) {
    this.limits.update((limits) => limits.filter((item) => item.id != limit.id));
    this.dataSource.data = this.limits();

    this.tenantContractsApi
      .tenantContractsRemoveLimit(this.tenantId!.toString(), limit.contractId!.toString(), limit.id!.toString())
      .pipe(
        map((_) => {
          this.sb.open('Contract Limit Deleted Successfully');
        }),
        catchError((_: ApiError) => {
          this.displayErrorMessage('Error deleting contract limit');
          return of(null);
        }),
      )
      .subscribe();
  }

  editLimit(limit: TenantContractLimitModel) {
    this.openLimitDialog(limit, 'edit');
  }

  createLimit() {
    this.openLimitDialog(null, 'create');
  }

  displayErrorMessage(message: string) {
    this.sb.open(message, undefined, { duration: 3000, panelClass: SB_PANEL_MESSAGE });
  }

  openLimitDialog(givenLimit: TenantContractLimitModel | null, action: string) {
    const dialog = this.dialog.open(ContractLimitAddDialogComponent, {
      data: {
        action: action,
        limit: givenLimit,
      },
    });

    dialog.afterClosed().subscribe((result: TenantContractLimitModel) => {
      if (result) {
        if (givenLimit !== null) {
          givenLimit.category = result.category;
          givenLimit.code = result.code;
          givenLimit.limitation = result.limitation;
          givenLimit.priority = result.priority;
        }

        if (action == 'edit') {
          const updateCommand = this.buildUpdateContractLimitCommand(givenLimit!);
          this.tenantContractsApi
            .tenantContractsUpdateLimit(
              this.tenantId!.toString(),
              this.contractId!.toString(),
              givenLimit!.id!.toString(),
              updateCommand,
            )
            .pipe(
              map((_) => {
                this.sb.open('Contract Limit Updated Successfully');
              }),
              catchError((_: ApiError) => {
                this.displayErrorMessage('Error updating contract limit');
                return of(null);
              }),
            )
            .subscribe();
        }

        if (action == 'create') {
          const createCommand = this.buildCreateContractLimitCommand(result!);
          this.tenantContractsApi
            .tenantContractsCreateLimit(this.tenantId!.toString(), this.contractId!.toString(), createCommand)
            .pipe(
              map((createResult) => {
                this.sb.open('Contract Limit Created Successfully');
                result.id = createResult;
                this.limits.update((limits) => [...limits, result]);
                this.dataSource.data = this.limits();
              }),
              catchError((_: ApiError) => {
                this.displayErrorMessage('Error creating contract limit');
                return of(null);
              }),
            )
            .subscribe();
        }
      }
    });
  }
}
